import {
    AgentAPIResponse,
    MessageOutput,
    Conversation,
    Interaction,
    FileInput,
    Document as ApiDocument,
    Folder as ApiFolder,
    FileOutput,
    UploadProgressEvent,
    YpfImportResult as ApiYpfImportResult,
    GoogleAuthResponse,
    GoogleCallbackResponse,
    SignedUrlResponse,
    FolderStatistics,
} from '@quadminds/client-apis/agents';
import { agentsApi } from './agents-api.service';
import { getAuthService } from './auth.service';
import { ErrorResponseData } from '../interfaces/axios.response';
import { YpfImportErrorDetail } from '../interfaces/ypf';
import { ConversationItem } from '../types/Conversation/Conversation';
import { FileStatus, UploadDestination } from '../types/File/File.types';
import { buildAgentsHeaders, rememberAccessToken } from '../utils/agentsRequest';
import { FileUploadError } from '../utils/fileError';

// Local type definitions to maintain compatibility with app-specific types
type Document = {
    _id: string;
    id?: string;
    title: string | null;
    name: string;
    filename?: string | null;
    description: string;
    file_type: string;
    file_size: number;
    status: FileStatus;
    active: boolean;
    summary: string | null;
    tags: string[];
    chunk_count: number | null;
    owner_id: string;
    uploaded_by:
        | {
              userId: string;
              fullName: string;
          }
        | string
        | null;
    organization_id: string;
    created_at: string;
    updated_at: string | null;
    processing_metadata: {
        processing_time: number;
        completion_status: string;
        chunking_strategy: string;
        error_message: string | null;
    };
    parent_id: string | null;
    content_metadata: {
        page_count: number;
        word_count: number;
        character_count: number;
        language: string;
        entities: unknown[];
    };
};

type Folder = {
    id: string;
    name: string;
    type: 'folder';
    created_at: string;
    updated_at: string;
    parent_id: string | null;
    owner_id: string;
    uploaded_by: {
        userId: string;
        fullName: string;
    };
    organization_id: string;
    child_count: number;
};

type YpfImportResult = {
    file_name: string;
    file_size_mb: number;
    imported_rows: {
        total: number;
        new_records: number;
        updated_records: number;
    };
    error_summary: {
        total_errors: number;
        total_error_rows: number;
        error_row_numbers: number[];
        details: YpfImportErrorDetail[];
    };
};

/**
 * Type adapters to convert between client API types and mindy-app types
 * Provides a clean abstraction layer between the shared client API and app-specific interfaces
 */

// Extended types to handle MongoDB's _id field that may be returned by the API
type ApiDocumentWithId = ApiDocument & { _id?: string };
type ApiConversationWithId = Conversation & { _id?: string };

/**
 * Converts API interaction to app-specific interaction format
 */
/**
 * Converts API interaction to app-specific interaction format
 */
const adaptInteraction = (apiInteraction: Interaction): Interaction => ({
    _id: apiInteraction._id || '',
    conversation_id: apiInteraction.conversation_id || '',
    organization_id: apiInteraction.organization_id || '',
    user_id: apiInteraction.user_id || '',
    agent_id: apiInteraction.agent_id || '',
    user_message: apiInteraction.user_message || '',
    agent_response: apiInteraction.agent_response || '',
    context: apiInteraction.context || [],
    tools_used: apiInteraction.tools_used || [],
    metadata: apiInteraction.metadata || {},
    usage_metadata: apiInteraction.usage_metadata || {},
    rating: apiInteraction.rating || '',
    feedback_comment: apiInteraction.feedback_comment || '',
    rated_at: apiInteraction.rated_at || '',
    attachments: apiInteraction.attachments || undefined,
});

/**
 * Converts API conversation to app-specific conversation format
 * Handles both MongoDB _id and REST API id fields for compatibility
 */
const adaptConversation = (apiConversation: ApiConversationWithId): Conversation => {
    const conversationId = apiConversation._id || '';

    return {
        _id: conversationId,
        user_id: '', // Not provided by API
        title: apiConversation.title,
        status: apiConversation.status,
        interactions: apiConversation.interactions?.map(adaptInteraction) || [],
        metadata: apiConversation.metadata || {},
        usage_metadata: {}, // Not provided by API
        created_at: apiConversation.created_at || '',
        updated_at: apiConversation.updated_at || '',
    };
};

/**
 * Converts API document to app-specific document format
 * Handles both MongoDB _id and REST API id fields for compatibility
 */
const adaptDocument = (apiDocument: ApiDocumentWithId): Document => {
    const documentId = apiDocument._id || apiDocument.id || '';

    return {
        _id: documentId,
        title: apiDocument.title || apiDocument.name || apiDocument.filename || null,
        name: apiDocument.name || apiDocument.filename || '',
        description: '', // This field is not in the API response
        file_type: '', // This field is not in the API response, could be derived from filename
        file_size: apiDocument.size || 0,
        status: (apiDocument.status as FileStatus) || 'uploaded',
        active: apiDocument.active ?? true,
        summary: apiDocument.summary || '',
        tags: apiDocument.tags || [],
        chunk_count: apiDocument.chunk_count || 0,
        owner_id: apiDocument.owner_id || '',
        uploaded_by: apiDocument.uploaded_by || {
            userId: '',
            fullName: '',
        },
        organization_id: apiDocument.organization_id || '',
        created_at: apiDocument.created_at || '',
        updated_at: apiDocument.updated_at || '',
        processing_metadata: {
            processing_time: apiDocument.processing_time_ms || 0,
            completion_status: apiDocument.processing_metadata?.processing_status || '',
            chunking_strategy: '',
            error_message: apiDocument.error || null,
        },
        parent_id: apiDocument.parent_id || null,
        content_metadata: {
            page_count: apiDocument.page_count || 0,
            word_count: 0, // Not provided by API
            character_count: 0, // Not provided by API
            language: '',
            entities: [],
        },
    };
};

/**
 * Handles error details conversion - supports both structured objects and legacy string format
 */
const adaptErrorDetails = (details: unknown[]): YpfImportErrorDetail[] => {
    return details.map((detail) => {
        // If it's already a structured object (new backend response format)
        if (typeof detail === 'object' && detail !== null && 'errorCode' in detail) {
            const structuredDetail = detail as YpfImportErrorDetail;
            return {
                rowNumber: structuredDetail.rowNumber || 0,
                originName: structuredDetail.originName || '',
                originCode: structuredDetail.originCode || '',
                destinationCode: structuredDetail.destinationCode || '',
                errorCode: structuredDetail.errorCode || 'UNKNOWN_ERROR',
                errorMessage: structuredDetail.errorMessage || '',
            };
        }

        // Legacy string format handling (fallback)
        if (typeof detail === 'string') {
            const regex = /^(\d+)\s{0,10}-\s{0,10}([^\n\r]*)$/;
            const rowErrorMatch = regex.exec(detail);
            if (rowErrorMatch) {
                const rowNumber = Number.parseInt(rowErrorMatch[1], 10);
                const errorPart = rowErrorMatch[2].trim();

                const errorCodeMap: Record<string, string> = {
                    'Origen no encontrado': 'ORIGIN_NOT_FOUND',
                    'Destino no encontrado': 'DESTINATION_NOT_FOUND',
                    'Se encontró más de un origen': 'MORE_THAN_ONE_ORIGIN_FOUND',
                    'Se encontró más de un destino': 'MORE_THAN_ONE_DESTINATION_FOUND',
                    'El código de destino no cumple con la longitud mínima': 'DESTINATION_MIN_LENGTH_NOT_MET',
                };

                const errorCode = errorCodeMap[errorPart] || errorPart;

                return {
                    rowNumber,
                    originName: '',
                    originCode: '',
                    destinationCode: '',
                    errorCode,
                    errorMessage: detail,
                };
            }

            return {
                rowNumber: 0,
                originName: '',
                originCode: '',
                destinationCode: '',
                errorCode: detail,
                errorMessage: detail,
            };
        }

        // Fallback for unexpected formats
        return {
            rowNumber: 0,
            originName: '',
            originCode: '',
            destinationCode: '',
            errorCode: 'PARSE_ERROR',
            errorMessage: String(detail),
        };
    });
};

/**
 * Converts API YPF import result to app-specific format
 */
/**
 * Converts API YPF import result to app-specific format
 */
const adaptYpfImportResult = (apiResult: ApiYpfImportResult): YpfImportResult => ({
    file_name: apiResult.file_name || '',
    file_size_mb: apiResult.file_size_mb || 0,
    imported_rows: {
        total: apiResult.imported_rows?.total || 0,
        new_records: apiResult.imported_rows?.new_records || 0,
        updated_records: apiResult.imported_rows?.updated_records || 0,
    },
    error_summary: {
        total_errors: apiResult.error_summary?.total_errors || 0,
        total_error_rows: apiResult.error_summary?.total_error_rows || 0,
        error_row_numbers: apiResult.error_summary?.error_row_numbers || [],
        details: adaptErrorDetails(apiResult.error_summary?.details || []),
    },
});

/**
 * Chat service wrapper that provides a clean abstraction layer over the shared AgentsApi client.
 *
 * This service consolidates all agent-related operations and provides:
 * - Type-safe conversion between shared API types and app-specific interfaces
 * - Centralized error handling with meaningful error messages
 * - Automatic authentication token refresh on 401 responses
 * - Progress tracking for file uploads
 * - Clean separation of concerns between API client and application layer
 *
 * Key Features:
 * - Knowledge and logistics messaging
 * - Conversation management (CRUD operations)
 * - Document and folder operations with pagination
 * - File uploads with progress callbacks
 * - Google Drive integration
 * - YPF template operations
 * - Rating and feedback system
 *
 * All methods use the shared @quadminds/client-apis/agents library underneath
 * while providing the interface expected by the mindy-app components.
 */

/**
 * Chat service wrapper that provides the same interface as the original ChatService
 * but uses the new AgentsApi client underneath with enhanced error handling and retry logic.
 */
class ChatService {
    private readonly maxRetries = 3;
    private retryCount = 0;

    constructor() {
        this.setupAuthInterceptor();
    }

    // === Messaging Methods ===

    private buildAuthHeaders(extraHeaders: Record<string, string> = {}): Record<string, string> {
        return buildAgentsHeaders({ extraHeaders });
    }

    /**
     * Send a knowledge-based message using RAG (Retrieval Augmented Generation)
     */
    async sendKnowledgeMessage(
        message: MessageOutput,
        requestId?: string,
        signal?: AbortSignal,
    ): Promise<AgentAPIResponse> {
        try {
            if (message.knowledge_destination === UploadDestination.BETA) {
                return await agentsApi.sendKnowledgeMessageV2(message);
            }
            return await agentsApi.sendKnowledgeMessage(message);
        } catch (error) {
            const axiosError = error as { code?: string; name?: string };
            if (
                axiosError.code === 'ERR_CANCELED' ||
                axiosError.name === 'AbortError' ||
                axiosError.name === 'CanceledError'
            ) {
                throw error;
            }
            throw new Error('Failed to send knowledge message:, ' + (error as Error).message);
        }
    }

    /**
     * Cancel an in-flight knowledge request by request_id.
     */
    async cancelKnowledgeMessage(requestId: string): Promise<{ conversation_id?: string }> {
        try {
            const response = await agentsApi.httpClient.post(`/knowledge/assistant/${requestId}/cancel`, undefined, {
                headers: this.buildAuthHeaders({
                    'Content-Type': 'application/json',
                }),
            });

            if (!response.status) {
                return {};
            }

            return response.data as { conversation_id?: string };
        } catch (error) {
            const responseStatus = (error as { response?: { status?: number } })?.response?.status;
            if (responseStatus === 404) {
                return {};
            }
            throw new Error('Failed to cancel knowledge message: ' + (error as Error).message);
        }
    }

    /**
     * Send a logistics-specific message
     */
    async sendLogisticsMessage(message: MessageOutput): Promise<AgentAPIResponse> {
        try {
            return await agentsApi.sendLogisticsMessage(message);
        } catch (error) {
            throw new Error('Failed to send logistics message: ' + (error as Error).message);
        }
    }

    // === Conversation Management ===

    /**
     * Retrieve all conversations for the current user
     */
    async getConversations(page: number, pageSize: number): Promise<Conversation[]> {
        try {
            const apiConversations = (await agentsApi.getConversations(page, pageSize)) as ApiConversationWithId[];
            return apiConversations.map(adaptConversation);
        } catch (error) {
            throw new Error('Failed to get conversations: ' + (error as Error).message);
        }
    }

    /**
     * Retrieve a specific conversation by ID
     */
    async getConversationBy(id: string): Promise<Conversation> {
        try {
            const apiConversation = (await agentsApi.getConversationById(id)) as ApiConversationWithId;
            return adaptConversation(apiConversation);
        } catch (error) {
            throw new Error('Failed to get conversation: ' + (error as Error).message);
        }
    }

    /**
     * Update conversation title and metadata
     */
    async patchConversationTitle(
        conversationId: string,
        newTitle: string,
        data: ConversationItem,
    ): Promise<Conversation> {
        try {
            const payload = {
                title: newTitle,
                status: data.status,
                metadata: data.metadata,
            };
            const apiConversation = (await agentsApi.updateConversation(
                conversationId,
                payload,
            )) as ApiConversationWithId;
            return adaptConversation(apiConversation);
        } catch (error) {
            throw new Error('Failed to patch conversation: ' + (error as Error).message);
        }
    }

    /**
     * Delete a conversation by ID
     */
    async deleteConversation(conversationId: string): Promise<void> {
        try {
            await agentsApi.deleteConversation(conversationId);
        } catch (error) {
            throw new Error('Failed to delete conversation: ' + (error as Error).message);
        }
    }

    /**
     * Rate a conversation response (positive/negative feedback)
     */
    async rateResponse(
        conversationId: string,
        interactionId: string,
        rating: 'positive' | 'negative',
        feedback_comment: string,
        agent_version: string,
        retries = 3,
        delayMs = 1000,
    ): Promise<unknown> {
        try {
            return await agentsApi.rateResponse(conversationId, interactionId, {
                rating,
                feedback_comment,
                agent_version,
            });
        } catch (error) {
            const isErrorFeedback = (error as { response?: { status?: number } })?.response?.status === 404;
            if (isErrorFeedback && retries > 0) {
                await new Promise((resolve) => setTimeout(resolve, delayMs));
                return this.rateResponse(
                    conversationId,
                    interactionId,
                    rating,
                    feedback_comment,
                    agent_version,
                    retries - 1,
                    delayMs * 2,
                );
            }
            throw new Error('Failed to rate response, ' + (error as Error).message);
        }
    }

    // === File Upload Methods ===

    /**
     * Upload files for ephemeral conversation use
     */
    async uploadFile(files: File[], conversationId?: string): Promise<FileInput> {
        try {
            return await agentsApi.uploadFile(files, conversationId);
        } catch (error) {
            throw new Error('Failed to upload file, ' + (error as Error).message);
        }
    }

    // === Document Management Methods ===

    /**
     * Retrieve knowledge documents with pagination and filtering
     */
    /**
     * Retrieves knowledge documents with pagination
     * @param organizationId The organization ID
     * @param params Query parameters for filtering documents
     * @returns A paginated list of documents with folders
     */
    async getKnowledgeDocuments(
        organizationId: string,
        params?: Record<string, unknown>,
    ): Promise<{
        data: Document[];
        folders: Folder[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            total_pages: number;
        };
        meta: {
            total_folders: number;
            total_documents: number;
            path: { id: string; name: string }[];
        };
    }> {
        try {
            // Extract individual parameters to match the API DTO structure
            const page = params?.['page'] ? Number(params['page']) : 1;
            const pageSize = params?.['page_size'] ? Number(params['page_size']) : 10;

            // Build the API call with individual parameters instead of nested filters
            const apiResult = await agentsApi.getKnowledgeDocuments(
                page,
                pageSize,
                organizationId,
                params, // Pass all params directly to the API client
            );

            // Convert API folders to app-specific Folder type
            const appFolders: Folder[] =
                apiResult.folders?.map((folder: ApiFolder) => ({
                    id: folder.id,
                    name: folder.name,
                    type: 'folder',
                    parent_id: folder.parent_id || null,
                    owner_id: folder.owner_id || '',
                    uploaded_by:
                        typeof folder.uploaded_by === 'string'
                            ? { userId: '', fullName: folder.uploaded_by }
                            : folder.uploaded_by || { userId: '', fullName: '' },
                    organization_id: folder.organization_id,
                    child_count: folder.child_count || 0,
                    created_at: folder.created_at || '',
                    updated_at: folder.updated_at || '',
                })) || [];

            // Convert documents from API format to app format
            return {
                data: apiResult.data.map(adaptDocument),
                folders: appFolders,
                pagination: {
                    page: apiResult.pagination.page,
                    limit: apiResult.pagination.page_size,
                    total: apiResult.pagination.total_documents,
                    total_pages: apiResult.pagination.total_pages,
                },
                meta: apiResult.meta,
            };
        } catch (error) {
            throw new Error('Failed to get knowledge documents: ' + (error as Error).message);
        }
    }

    async uploadKnowledgeFile(
        file: File,
        organizationId: string,
        handleProgressEvent?: (event: UploadProgressEvent & { name?: string }) => void,
        parent_id?: string,
        destination: UploadDestination = UploadDestination.STANDARD,
    ): Promise<FileOutput> {
        try {
            if (destination === UploadDestination.BETA) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('organization_id', organizationId);
                if (parent_id) {
                    formData.append('parent_id', parent_id);
                }

                const response = await agentsApi.httpClient.post('/documents/v2/upload', formData, {
                    onUploadProgress: (event) =>
                        handleProgressEvent?.({
                            ...(event as unknown as UploadProgressEvent),
                            name: file.name,
                        }),
                });

                return response.data as FileOutput;
            }

            const apiResult = await agentsApi.uploadKnowledgeFile(file, organizationId, handleProgressEvent, parent_id);
            return apiResult;
        } catch (error) {
            if (error instanceof Error && error.message.includes('Failed to upload file')) {
                const ErrorCode = 'BAD_FILE'; // Default error code
                const errorMessage = new FileUploadError('Failed to upload file', file.name, ErrorCode);
                throw errorMessage;
            }
            // Re-throw original error to preserve response.data.detail from the API
            throw error;
        }
    }

    async updateKnowledgeDocument(
        documentId: string,
        organizationId: string,
        payload: Partial<Document>,
    ): Promise<Document> {
        try {
            const apiPayload: Partial<ApiDocument> = {
                name: payload.name,
                title: payload.title,
                summary: payload.summary,
                tags: payload.tags,
                parent_id: payload.parent_id === null ? null : payload.parent_id || undefined,
                active: payload.active,
                ...(payload.processing_metadata && {
                    processing_metadata: {
                        processing_status: payload.processing_metadata.completion_status,
                    },
                }),
            };
            const apiResult = await agentsApi.updateKnowledgeDocument(documentId, organizationId, apiPayload);

            return adaptDocument(apiResult);
        } catch (error) {
            console.error('Error updating knowledge document:', error);
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as { response: { data: ErrorResponseData } };
                const errorDetail = axiosError.response?.data?.detail;

                if (errorDetail) {
                    throw new Error(errorDetail);
                }
            }

            throw new Error('Failed to update knowledge document: ' + (error as Error).message);
        }
    }

    async deleteKnowledgeDocument(documentId: string, organizationId: string): Promise<unknown> {
        try {
            return await agentsApi.deleteKnowledgeDocument(documentId, organizationId);
        } catch (error) {
            throw new Error('Failed to delete knowledge documents: ' + (error as Error).message);
        }
    }

    // Folder Management Methods
    async createFolder(name: string, parentId?: string): Promise<unknown> {
        try {
            return await agentsApi.createFolder({ name, parent_id: parentId });
        } catch (error) {
            throw new Error('Failed to create a folder response: ' + (error as Error).message);
        }
    }

    async updateFolder(folderId: string, organizationId: string, name: string, parentId?: string): Promise<unknown> {
        try {
            return await agentsApi.updateFolder(folderId, organizationId, { name, parent_id: parentId });
        } catch (error) {
            throw new Error('Failed to update a folder response: ' + (error as Error).message);
        }
    }

    async deleteFolder(folderId: string, organizationId: string, recursive = false): Promise<void> {
        try {
            await agentsApi.deleteFolder(folderId, organizationId, recursive);
        } catch (error) {
            throw new Error('Failed to delete folder: ' + (error as Error).message);
        }
    }

    async moveDocumentToFolder(
        documentId: string,
        organizationId: string,
        destinationFolderId?: string,
    ): Promise<{ success: boolean; message?: string }> {
        if (!documentId?.trim()) {
            throw new Error('Document ID is required');
        }

        if (!organizationId?.trim()) {
            throw new Error('Organization ID is required');
        }

        try {
            const response = await agentsApi.moveDocumentToFolder(documentId, {
                organization_id: organizationId,
                destination_folder_id: destinationFolderId,
            });

            return {
                success: true,
                message: response.message || 'Document moved successfully',
            };
        } catch (error) {
            if (error instanceof Error) {
                const errorMessage = error.message;
                console.error('Failed to move document:', errorMessage);

                // Handle specific error cases based on common HTTP status patterns
                if (errorMessage.includes('400')) {
                    throw new Error(`Invalid request: ${errorMessage}`);
                } else if (errorMessage.includes('403')) {
                    throw new Error('You do not have permission to move this document');
                } else if (errorMessage.includes('404')) {
                    throw new Error('Document or destination folder not found');
                } else if (errorMessage.includes('409')) {
                    throw new Error('Cannot move document: conflict detected (may already exist in destination)');
                } else {
                    throw new Error(`Failed to move document: ${errorMessage}`);
                }
            } else {
                throw new Error('An unexpected error occurred while moving the document: ' + (error as Error).message);
            }
        }
    }

    // Google Auth Methods
    async redirectToGoogleAuth(redirectUrl: string): Promise<{ data: GoogleAuthResponse }> {
        try {
            const data = await agentsApi.redirectToGoogleAuth(redirectUrl);
            return { data };
        } catch (error) {
            throw new Error('Failed to redirect to Google authentication: ' + (error as Error).message);
        }
    }

    async resolveGoogleAuthCode(code: string, state: string): Promise<GoogleCallbackResponse> {
        try {
            return await agentsApi.resolveGoogleAuthCode(code, state);
        } catch (error) {
            throw new Error('Failed to resolve Google authentication code: ' + (error as Error).message);
        }
    }

    async importDocumentsFromGoogle(
        fileId: string,
        parentId: string | null,
        fileName: string,
        externalId: string,
        handleProgressEvent?: (event: UploadProgressEvent) => void,
    ): Promise<FileOutput> {
        if (localStorage.getItem('googleAccessToken') === null) {
            throw new Error('Google access token is not available');
        }

        try {
            const apiResult = await agentsApi.importDocumentsFromGoogle(
                {
                    file_id: fileId,
                    parent_id: parentId,
                    file_name: fileName,
                    external_id: externalId,
                },
                handleProgressEvent,
            );
            return apiResult;
        } catch (error) {
            if (error instanceof Error) {
                const errorMessage = new FileUploadError('Failed to upload file', fileName, error.message);
                throw errorMessage;
            }
            throw new Error('Failed to import documents from Google');
        }
    }

    async importDocumentsFromUri(
        uri: string,
        externalId: string,
        parentId: string | null,
        handleProgressEvent?: (event: UploadProgressEvent) => void,
    ): Promise<FileOutput> {
        try {
            const apiResult = await agentsApi.importDocumentsFromUri(
                {
                    url: uri,
                    auto_detect_strategy: true,
                    external_id: externalId,
                    parent_id: parentId,
                },
                handleProgressEvent,
            );
            return apiResult;
        } catch (error) {
            if (error instanceof Error) {
                const ErrorCode = 'BAD_FILE';
                const errorMessage = new FileUploadError('Failed to upload file', uri, ErrorCode);
                throw errorMessage;
            }
            throw new Error('Failed to import documents from URI');
        }
    }

    // Statistics and Utilities
    async folderStatistics(folderId?: string): Promise<FolderStatistics> {
        try {
            return await agentsApi.getFolderStatistics(folderId);
        } catch (error) {
            throw new Error('Failed to get folder statistics: ' + (error as Error).message);
        }
    }

    async getSignedDocmentUrl(documentId: string): Promise<SignedUrlResponse> {
        try {
            return await agentsApi.getSignedDocumentUrl(documentId);
        } catch (error) {
            throw new Error('Failed to get signed document URL: ' + (error as Error).message);
        }
    }

    // YPF Methods
    async uploadYpfFile(
        file: File,
        handleProgressEvent?: (event: UploadProgressEvent) => void,
    ): Promise<YpfImportResult> {
        try {
            const apiResult = await agentsApi.uploadYpfFile(file, handleProgressEvent);
            return adaptYpfImportResult(apiResult);
        } catch (error) {
            if (error instanceof Error) {
                const ErrorCode = 'BAD_FILE';
                const errorMessage = new FileUploadError('Failed to upload file', file.name, ErrorCode);
                throw errorMessage;
            }
            throw new Error('Failed to upload file');
        }
    }

    async getYpfTemplate(): Promise<Blob> {
        try {
            return await agentsApi.getYpfTemplate();
        } catch (error) {
            throw new Error('Failed to download template: ' + (error as Error).message);
        }
    }

    // === Private Methods ===

    /**
     * Setup authentication interceptor for automatic token refresh on 401 errors
     */
    private setupAuthInterceptor() {
        agentsApi.httpClient.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (
                    error.response?.status === 401 &&
                    error.response?.data?.message &&
                    error.response?.data?.message !== 'API Key not provided' &&
                    !originalRequest?.isRetry &&
                    this.retryCount < this.maxRetries
                ) {
                    if (originalRequest) {
                        originalRequest.isRetry = true;
                    }
                    this.retryCount++;

                    const newToken = await getAuthService().refreshToken();
                    if (newToken?.accessToken) {
                        rememberAccessToken(newToken.accessToken);
                        if (newToken?.refreshToken) {
                            localStorage.setItem('refreshToken', newToken.refreshToken);
                        }
                        if (error.config) {
                            error.config.headers['Authorization'] = `Bearer ${newToken.accessToken}`;
                            return agentsApi.httpClient(error.config);
                        }
                        throw new Error('Original request configuration is missing');
                    }
                    throw new Error('Failed to refresh token');
                }
                throw error;
            },
        );
    }
}

export default ChatService;
